import { useContext, useEffect, useState } from "react";
import trans, { isRTL } from "../../../utils";
import { exportContext } from "../../../context/ExportDataContext";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useLazyGetVehicleRoutesQuery } from "../../../Redux/service/NewReports/Reports";
import AntdTable from "../../antd/Table/Table";
import axios from "axios";
import { DistanceUnit, SpeedUnit } from "../../../JsHelpers/PlatformUnits";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const RouteSheetTable = ({
	selectedids,
	startDate,
	endDate,
	isShown,
	currentPage,
	setCurrentPage,
}) => {
	const inf = useSelector((state) => state.users.user);
	const { setFileName, setExportIds, setStartDateE, setEndDateE } =
		useContext(exportContext);
	const {
		setSort: SetSortFilter,
		searchQuery,
		setSearchQuery,
		limit,
		setLimit,
	} = useContext(filtrationcontext);
	const fieldMapping = {
		"attributes.distance": "distance",
		"attributes.totalDistance": "totalDistance",
		"attributes.ignition": "ignition",
		"attributes.odometer": "odometer",
		"attributes.event": "event",
		"attributes.power": "power",
		"attributes.battery": "battery",
	};

	const [sort, setSort] = useState({
		by: "",
		order: "",
	});

	const [trigger, { data }] = useLazyGetVehicleRoutesQuery();
	const [loading, setLoading] = useState(false);
	const [geoCodes, setGeoCode] = useState({});

	const truncateItem = (text) => {
		const maxLength = 25;
		return text?.length > maxLength
			? text.substring(0, maxLength) + "..."
			: text;
	};

	const fetchGeoCodePosition = async (lat, lon, id) => {
		try {
			const response = await axios.get(
				`https://nominatim.openstreetmap.org/reverse.php?lat=${lat}&lon=${lon}&zoom=18&format=jsonv2`
			);
			setGeoCode((prev) => ({
				...prev,
				[id]: response.data.display_name,
			}));
		} catch (error) {
			toast.error(trans("Error fetching geocode position:"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		}
	};

	const columns = [
		{
			title: trans("Protocol"),
			dataIndex: "protocol",
			width: 150,
			fixed: !isRTL ? "left" : undefined,
			render: (protocol) => (
				<span className={"table-element-name ps-0"}>{protocol}</span>
			),
		},
		{
			title: trans("Group"),
			dataIndex: "groupName",
			width: 150,
			render: (groupName) => groupName || trans("Ungrouped Vehicles"),
		},
		{
			title: trans("Date"),
			dataIndex: "fixTime",
			width: 180,
			sorter: true,
			render: (fixTime) => {
				const date = new Date(fixTime);
				return (
					<span className="table-element-date">
						{date.toLocaleDateString()} {date.toLocaleTimeString()}
					</span>
				);
			},
		},
		{
			title: trans("Address"),
			dataIndex: "fixTime",
			width: 250,
			render: (id, record) => {
				const geoAddress = geoCodes[id];
				return (
					<span
						className="text-primary cursor"
						onClick={() =>
							fetchGeoCodePosition(record.latitude, record.longitude, id)
						}
						title={geoAddress}
					>
						{geoAddress
							? truncateItem(geoAddress)
							: trans("Get current address")}
					</span>
				);
			},
		},
		{
			title: trans("Speed"),
			dataIndex: "speed",
			sorter: true,
			width: 120,
			render: (speed = 0) =>
				SpeedUnit(speed, inf?.attributes?.speedUnit || "kn"),
		},
		{
			title: trans("Distance"),
			dataIndex: ["attributes", "distance"],
			sorter: true,
			width: 120,
			render: (distance = 0) =>
				DistanceUnit(distance, inf?.attributes?.distanceUnit || "km"),
		},
		{
			title: trans("Total Distance"),
			dataIndex: ["attributes", "totalDistance"],
			sorter: true,
			width: 150,
			render: (totalDistance = 0) =>
				DistanceUnit(totalDistance, inf?.attributes?.distanceUnit || "km"),
		},
		{
			title: trans("Ignition"),
			dataIndex: ["attributes", "ignition"],
			sorter: true,
			width: 100,
			render: (ignition) => (ignition ? "ON" : "OFF"),
		},
		{
			title: trans("Odometer"),
			dataIndex: ["attributes", "odometer"],
			sorter: true,
			width: 150,
			render: (odometer = 0) =>
				DistanceUnit(odometer, inf?.attributes?.distanceUnit || "km"),
		},
		{
			title: trans("Event"),
			dataIndex: ["attributes", "event"],
			sorter: true,
			width: 150,
		},
		{
			title: trans("Power"),
			dataIndex: ["attributes", "power"],
			sorter: true,
			width: 100,
			render: (power = 0) => power.toFixed(2),
		},
		{
			title: trans("battery"),
			dataIndex: ["attributes", "battery"],
			sorter: true,
			width: 100,
			render: (battery = 0) => battery.toFixed(2),
		},
	];

	useEffect(() => {
		if (isShown && startDate && endDate && selectedids.length > 0) {
			const from = new Date(startDate).toISOString().replace(/:/g, "%3A");
			const to = new Date(endDate).toISOString().replace(/:/g, "%3A");
			setLoading(true);
			trigger(
				{
					from,
					to,
					selectedids,
					page: currentPage,
					pageSize: limit,
					sortBy: sort.by,
					sortOrder: sort.order,
					search: searchQuery,
				},
				true
			).finally(() => setLoading(false));
		}
	}, [
		isShown,
		startDate,
		endDate,
		selectedids,
		currentPage,
		limit,
		sort.by,
		sort.order,
		searchQuery,
	]);

	useEffect(() => {
		return () => {
			SetSortFilter({ type: "", value: "" });
			localStorage.setItem("page", 1);
			setSearchQuery("");
		};
	}, [SetSortFilter, setSearchQuery]);

	useEffect(() => {
		if (data) {
			setFileName("Routes Report");
			setExportIds(selectedids);
			setStartDateE(startDate);
			setEndDateE(endDate);
		}
	}, [data, startDate, endDate, selectedids]);

	const handleTableChange = (pagination, filters, sorter) => {
		if (sorter.order) {
			const apiField = fieldMapping[sorter.field] || sorter.field;
			setSort({
				by: apiField,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSort({
				by: "",
				order: "",
			});
		}
	};

	return (
		<div className="bg-white-rounded-top mt-3 table-parent">
			<AntdTable
				columns={columns}
				dataSource={data?.data || []}
				loading={loading}
				pageSize={limit}
				totalItems={data?.totalCount}
				currentPage={currentPage}
				onPageChange={(page) => setCurrentPage(page)}
				onLimitChange={(newLimit) => {
					setLimit(newLimit);
					setCurrentPage(1);
				}}
				onTableChange={handleTableChange}
				scrollY={350}
			/>
		</div>
	);
};

export default RouteSheetTable;
